import { useRef, useEffect, useState, useCallback } from 'react';
import { MapPin, Loader2, X, Pencil, Check, Search } from 'lucide-react';

// Extend Window interface for Google Maps
declare global {
  interface Window {
    google?: typeof google;
    __addressAutocompleteDebug?: {
      scriptLoadAttempts: number;
      scriptLoadErrors: string[];
      importLibraryAttempts: number;
      placeAutocompleteCreateAttempts: number;
      placeSelectEvents: number;
      lastError: string | null;
      elementInfo: string | null;
    };
  }
}

// Initialize debug object on window for production debugging
if (typeof window !== 'undefined' && !window.__addressAutocompleteDebug) {
  window.__addressAutocompleteDebug = {
    scriptLoadAttempts: 0,
    scriptLoadErrors: [],
    importLibraryAttempts: 0,
    placeAutocompleteCreateAttempts: 0,
    placeSelectEvents: 0,
    lastError: null,
    elementInfo: null,
  };
}

// Debug logger that logs to console and stores in window for inspection
function debugLog(level: 'info' | 'warn' | 'error', message: string, data?: unknown) {
  const timestamp = new Date().toISOString();
  const prefix = `[AddressAutocomplete ${timestamp}]`;

  if (level === 'error') {
    console.error(prefix, message, data ?? '');
    if (window.__addressAutocompleteDebug) {
      window.__addressAutocompleteDebug.lastError = `${message} ${JSON.stringify(data ?? {})}`;
    }
  } else if (level === 'warn') {
    console.warn(prefix, message, data ?? '');
  } else {
    console.log(prefix, message, data ?? '');
  }
}

export interface AddressData {
  address: string;
  placeId: string;
  lat: number;
  lng: number;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (address: AddressData | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Optional coordinates to bias search results toward */
  locationBias?: { lat: number; lng: number };
  /** If true, shows saved address with edit button instead of always showing search */
  singleAddressMode?: boolean;
  /** Shows saving indicator when true */
  isSaving?: boolean;
  /** Called after save completes successfully - triggers 'Saved' feedback */
  onSaveComplete?: () => void;
}

// Track script loading state globally to avoid duplicate loads
let scriptLoadPromise: Promise<google.maps.PlacesLibrary | null> | null = null;
let placesLibrary: google.maps.PlacesLibrary | null = null;

async function loadGoogleMapsScript(apiKey: string): Promise<google.maps.PlacesLibrary | null> {
  if (window.__addressAutocompleteDebug) {
    window.__addressAutocompleteDebug.scriptLoadAttempts++;
  }

  debugLog('info', 'loadGoogleMapsScript called', {
    hasApiKey: !!apiKey,
    apiKeyPrefix: apiKey ? apiKey.substring(0, 10) + '...' : 'none',
    hasCachedLibrary: !!placesLibrary,
    hasLoadPromise: !!scriptLoadPromise,
  });

  if (placesLibrary) {
    debugLog('info', 'Using cached places library');
    return placesLibrary;
  }

  if (scriptLoadPromise) {
    debugLog('info', 'Using existing script load promise');
    return scriptLoadPromise;
  }

  scriptLoadPromise = (async () => {
    // Check current state
    debugLog('info', 'Checking current Google Maps state', {
      hasGoogleObject: typeof window.google !== 'undefined',
      hasGoogleMaps: typeof window.google?.maps !== 'undefined',
      hasImportLibrary: typeof window.google?.maps?.importLibrary === 'function',
    });

    // Check if importLibrary is already available (script already loaded with loading=async)
    if (typeof window.google?.maps?.importLibrary === 'function') {
      debugLog('info', 'Google Maps already loaded, importing places library');
      if (window.__addressAutocompleteDebug) {
        window.__addressAutocompleteDebug.importLibraryAttempts++;
      }
      try {
        placesLibrary = await window.google.maps.importLibrary('places') as google.maps.PlacesLibrary;
        debugLog('info', 'Places library imported successfully', {
          hasPlaceAutocompleteElement: !!(placesLibrary as unknown as { PlaceAutocompleteElement?: unknown }).PlaceAutocompleteElement,
        });
        return placesLibrary;
      } catch (err) {
        debugLog('error', 'Failed to import places library', { error: String(err) });
        return null;
      }
    }

    // Check if script tag already exists
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      debugLog('warn', 'Google Maps script tag exists but importLibrary not available', {
        scriptSrc: (existingScript as HTMLScriptElement).src,
      });
    }

    // Load script with loading=async (Google recommended pattern)
    debugLog('info', 'Loading Google Maps script with loading=async');
    try {
      await new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        // IMPORTANT: Use loading=async WITHOUT callback or libraries parameter
        // This enables the importLibrary() method
        const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&v=beta`;
        script.src = scriptUrl;
        script.async = true;
        script.id = 'google-maps-script';

        script.onload = () => {
          debugLog('info', 'Google Maps script onload fired', {
            hasGoogleObject: typeof window.google !== 'undefined',
            hasGoogleMaps: typeof window.google?.maps !== 'undefined',
            hasImportLibrary: typeof window.google?.maps?.importLibrary === 'function',
          });
          resolve();
        };

        script.onerror = (event) => {
          const errorMsg = 'Failed to load Google Maps script';
          debugLog('error', errorMsg, { event: String(event) });
          if (window.__addressAutocompleteDebug) {
            window.__addressAutocompleteDebug.scriptLoadErrors.push(errorMsg);
          }
          reject(new Error(errorMsg));
        };

        document.head.appendChild(script);
        debugLog('info', 'Script tag appended to head', { scriptUrl });
      });
    } catch (err) {
      debugLog('error', 'Script loading promise rejected', { error: String(err) });
      return null;
    }

    // Wait for importLibrary to become available (Google Maps initializes asynchronously)
    debugLog('info', 'Waiting for importLibrary to become available');
    const waitForImportLibrary = async (maxAttempts = 100, intervalMs = 100): Promise<boolean> => {
      for (let i = 0; i < maxAttempts; i++) {
        const hasImportLibrary = typeof window.google?.maps?.importLibrary === 'function';
        if (i % 10 === 0) {
          debugLog('info', `Polling for importLibrary (attempt ${i + 1}/${maxAttempts})`, {
            hasGoogleObject: typeof window.google !== 'undefined',
            hasGoogleMaps: typeof window.google?.maps !== 'undefined',
            hasImportLibrary,
          });
        }
        if (hasImportLibrary) {
          debugLog('info', `importLibrary available after ${i + 1} attempts`);
          return true;
        }
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
      return false;
    };

    const importLibraryAvailable = await waitForImportLibrary();
    if (!importLibraryAvailable) {
      debugLog('error', 'importLibrary not available after 100 attempts (10 seconds)', {
        hasGoogleObject: typeof window.google !== 'undefined',
        hasGoogleMaps: typeof window.google?.maps !== 'undefined',
        googleMapsKeys: window.google?.maps ? Object.keys(window.google.maps).slice(0, 10) : [],
      });
      return null;
    }

    // Now use importLibrary to load the places library
    if (window.__addressAutocompleteDebug) {
      window.__addressAutocompleteDebug.importLibraryAttempts++;
    }
    try {
      debugLog('info', 'Importing places library via importLibrary');
      placesLibrary = await window.google.maps.importLibrary('places') as google.maps.PlacesLibrary;
      const libKeys = Object.keys(placesLibrary as unknown as object);
      debugLog('info', 'Places library ready', {
        libraryKeys: libKeys.slice(0, 10),
        hasPlaceAutocompleteElement: !!(placesLibrary as unknown as { PlaceAutocompleteElement?: unknown }).PlaceAutocompleteElement,
      });
      return placesLibrary;
    } catch (err) {
      debugLog('error', 'Failed to import places library', { error: String(err) });
      return null;
    }
  })();

  return scriptLoadPromise;
}

export function AddressAutocomplete({
  value,
  onChange,
  placeholder = 'Enter your home address',
  disabled = false,
  locationBias,
  singleAddressMode = false,
  isSaving = false,
}: AddressAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [places, setPlaces] = useState<google.maps.PlacesLibrary | null>(null);
  const [isEditing, setIsEditing] = useState(!value); // Start in edit mode if no value
  const [showSavedFeedback, setShowSavedFeedback] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const autocompleteElementRef = useRef<HTMLElement | null>(null);
  const fallbackInputRef = useRef<HTMLInputElement>(null);
  
  // Track when container is actually mounted (for re-init after isEditing changes)
  // Use a ref to avoid render loops when setting via callback ref
  const containerMountedRef = useRef(false);
  const [containerMounted, setContainerMounted] = useState(false);
  
  // Stable callback ref that doesn't trigger re-renders on every assignment
  const setContainerRef = useCallback((el: HTMLDivElement | null) => {
    containerRef.current = el;
    const isMounted = !!el;
    // Only update state if the mounted status actually changed
    if (containerMountedRef.current !== isMounted) {
      containerMountedRef.current = isMounted;
      setContainerMounted(isMounted);
    }
  }, []);

  // Track if place was selected from autocomplete to differentiate from manual entry
  const placeSelectedRef = useRef(false);
  // Track last saved value to prevent duplicate saves on blur
  const lastSavedValueRef = useRef(value);
  // Use ref for onChange to avoid recreating autocomplete on every parent re-render
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';


  // Handle save complete feedback
  useEffect(() => {
    if (!isSaving && lastSavedValueRef.current !== value && value) {
      // Save just completed
      setShowSavedFeedback(true);
      lastSavedValueRef.current = value;
      const timer = setTimeout(() => setShowSavedFeedback(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [isSaving, value]);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
    if (singleAddressMode) {
      // Exit edit mode when value is set externally (address saved)
      // Enter edit mode when value is cleared (no address)
      setIsEditing(!value);
    }
  }, [value, singleAddressMode]);

  // Load Google Maps script and places library
  useEffect(() => {
    if (!apiKey) {
      return;
    }

    loadGoogleMapsScript(apiKey)
      .then((lib) => {
        if (lib) {
          setPlaces(lib);
          setIsLoaded(true);
        } else {
          setLoadError('Places library not available');
        }
      })
      .catch((err) => setLoadError(err.message));
  }, [apiKey]);

  // Initialize PlaceAutocompleteElement (Places API New)
  // Uses gmp-place-autocomplete web component with gmp-select event
  useEffect(() => {
    debugLog('info', 'PlaceAutocompleteElement useEffect triggered', {
      isLoaded,
      hasPlaces: !!places,
      disabled,
      hasContainer: !!containerRef.current,
      containerMounted,
    });

    if (!isLoaded || !places || disabled || !containerRef.current || !containerMounted) {
      debugLog('info', 'Skipping PlaceAutocompleteElement init (conditions not met)');
      return;
    }

    const container = containerRef.current;

    // Clean existing element if any
    if (autocompleteElementRef.current && container.contains(autocompleteElementRef.current)) {
      container.removeChild(autocompleteElementRef.current);
      autocompleteElementRef.current = null;
    }

    debugLog('info', 'Creating <gmp-place-autocomplete> web component');

    if (window.__addressAutocompleteDebug) {
      window.__addressAutocompleteDebug.placeAutocompleteCreateAttempts++;
    }

    try {
      // Create the web component directly - this is more reliable than
      // trying to use places.PlaceAutocompleteElement constructor
      const autocompleteElement = document.createElement(
        'gmp-place-autocomplete'
      ) as HTMLElement & {
        value?: string;
        placeholder?: string;
      };

      // Basic styling
      autocompleteElement.setAttribute('style', 'display: block; width: 100%;');

      // Set placeholder
      if (placeholder) {
        autocompleteElement.setAttribute('placeholder', placeholder);
      }

      // Optional: bias search toward a point
      if (locationBias && locationBias.lat && locationBias.lng) {
        autocompleteElement.setAttribute(
          'location-bias',
          `point:${locationBias.lat},${locationBias.lng}`
        );
        debugLog('info', 'Location bias set', { lat: locationBias.lat, lng: locationBias.lng });
      }

      // Insert into container
      container.appendChild(autocompleteElement);
      autocompleteElementRef.current = autocompleteElement;

      debugLog('info', 'gmp-place-autocomplete element created and appended', {
        tagName: autocompleteElement.tagName,
      });

      // Keep a flag for cleanup
      let destroyed = false;

      // gmp-select handler (New Places API - this replaces deprecated gmp-placeselect)
      const handlePlaceSelect = async (event: Event) => {
        if (destroyed) return;

        if (window.__addressAutocompleteDebug) {
          window.__addressAutocompleteDebug.placeSelectEvents++;
        }
        debugLog('info', 'gmp-select event fired');

        try {
          // Google emits a CustomEvent with { placePrediction }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const anyEvent = event as any;
          const placePrediction =
            anyEvent.placePrediction ?? anyEvent.detail?.placePrediction;

          debugLog('info', 'Place prediction from event', {
            hasPlacePrediction: !!placePrediction,
            predictionKeys: placePrediction ? Object.keys(placePrediction).slice(0, 10) : [],
          });

          if (!placePrediction) {
            debugLog('warn', 'gmp-select event without placePrediction', { event: anyEvent });
            return;
          }

          // Convert prediction to a Place object (New Places API)
          const place = placePrediction.toPlace();
          debugLog('info', 'Converted prediction to Place, fetching fields...');
          
          await place.fetchFields({
            fields: ['id', 'displayName', 'formattedAddress', 'location'],
          });
          debugLog('info', 'fetchFields completed');

          // Extract location - handle both LatLng object and plain object
          const location = place.location as
            | google.maps.LatLng
            | { lat: number; lng: number }
            | null
            | undefined;

          if (!location) {
            debugLog('warn', 'Place has no location', { place });
            return;
          }

          // Handle LatLng.lat() method or plain object
          const lat =
            typeof (location as google.maps.LatLng).lat === 'function'
              ? (location as google.maps.LatLng).lat()
              : (location as { lat: number }).lat;

          const lng =
            typeof (location as google.maps.LatLng).lng === 'function'
              ? (location as google.maps.LatLng).lng()
              : (location as { lng: number }).lng;

          if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
            debugLog('warn', 'Invalid lat/lng from place.location', { location, lat, lng });
            return;
          }

          // Get address text - prefer formattedAddress, then displayName
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const placeAny = place as any;
          const addressText =
            placeAny.formattedAddress ||
            placeAny.displayName ||
            (placePrediction.text?.toString()) ||
            '';

          // Get place ID
          const placeId: string = placeAny.id || placePrediction.placeId || '';

          const addressData: AddressData = {
            address: addressText,
            placeId,
            lat,
            lng,
          };

          debugLog('info', 'Resolved place from gmp-select', addressData);

          // Track that we got a real place selection
          placeSelectedRef.current = true;
          lastSavedValueRef.current = addressData.address;
          setInputValue(addressData.address);

          // Notify parent – this triggers updateFamily.mutate()
          onChangeRef.current(addressData);

          // Exit edit mode after selection to show saved address feedback
          setIsEditing(false);
        } catch (err) {
          debugLog('error', 'Error handling gmp-select event', {
            error: String(err),
            stack: (err as Error).stack,
          });
        }
      };

      // Use gmp-select event (replaces deprecated gmp-placeselect)
      autocompleteElement.addEventListener('gmp-select', handlePlaceSelect as EventListener);

      // Add CSS to style the internal input (Google's web component uses shadow DOM)
      const styleId = 'gmp-autocomplete-styles';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          gmp-place-autocomplete {
            display: block !important;
            width: 100% !important;
            min-height: 42px !important;
          }
          gmp-place-autocomplete::part(input) {
            width: 100% !important;
            padding: 8px 12px 8px 36px !important;
            border: 1px solid #e2e8f0 !important;
            border-radius: 8px !important;
            font-size: 14px !important;
            outline: none !important;
          }
          gmp-place-autocomplete::part(input):focus {
            border-color: #15a2a2 !important;
            box-shadow: 0 0 0 2px rgba(21, 162, 162, 0.2) !important;
          }
        `;
        document.head.appendChild(style);
        debugLog('info', 'Injected gmp-autocomplete styles');
      }

      // Store element info for debugging
      if (window.__addressAutocompleteDebug) {
        setTimeout(() => {
          if (autocompleteElement && !destroyed) {
            const info = {
              tagName: autocompleteElement.tagName,
              isConnected: autocompleteElement.isConnected,
              attributes: Array.from(autocompleteElement.attributes).map((a) => ({
                name: a.name,
                value: a.value,
              })),
            };
            window.__addressAutocompleteDebug!.elementInfo = JSON.stringify(info);
            debugLog('info', 'Element info captured', info);
          }
        }, 1000);
      }

      // Cleanup
      return () => {
        destroyed = true;
        debugLog('info', 'Cleanup: removing gmp-place-autocomplete');
        autocompleteElement.removeEventListener('gmp-select', handlePlaceSelect as EventListener);
        if (container.contains(autocompleteElement)) {
          container.removeChild(autocompleteElement);
        }
        if (autocompleteElementRef.current === autocompleteElement) {
          autocompleteElementRef.current = null;
        }
      };
    } catch (err) {
      debugLog('error', 'PlaceAutocompleteElement creation failed', { error: String(err), stack: (err as Error).stack });
      setLoadError('Address autocomplete not available. Please enable Places API (New) in Google Cloud Console.');
    }
  // Note: containerMounted triggers re-init when container is attached to DOM
  // (fixes issue where isEditing changes but container ref isn't ready yet)
  }, [isLoaded, places, disabled, locationBias, singleAddressMode, placeholder, containerMounted]);

  const handleClear = useCallback(() => {
    setInputValue('');
    lastSavedValueRef.current = '';
    placeSelectedRef.current = false;
    onChange(null);
    // Return to edit mode so user can search for new address
    setIsEditing(true);
  }, [onChange]);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
    placeSelectedRef.current = false;
  }, []);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setInputValue(value); // Reset to saved value
    placeSelectedRef.current = false;
  }, [value]);

  const handleFallbackInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    placeSelectedRef.current = false;
  }, []);

  // Blur handler for fallback input
  const handleFallbackBlur = useCallback(() => {
    if (placeSelectedRef.current) {
      placeSelectedRef.current = false;
      return;
    }
    if (inputValue === lastSavedValueRef.current) {
      return;
    }
    if (!inputValue.trim()) {
      return;
    }
    const addressData: AddressData = {
      address: inputValue.trim(),
      placeId: '',
      lat: 0,
      lng: 0,
    };
    lastSavedValueRef.current = addressData.address;
    onChangeRef.current(addressData);
    if (singleAddressMode) {
      setIsEditing(false);
    }
  }, [inputValue, singleAddressMode]);

  // Render save state indicator
  const renderSaveState = () => {
    if (isSaving) {
      return (
        <div className="flex items-center gap-1 text-text-muted">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span className="text-xs">Saving...</span>
        </div>
      );
    }
    if (showSavedFeedback) {
      return (
        <div className="flex items-center gap-1 text-green-600">
          <Check className="w-3 h-3" />
          <span className="text-xs">Saved</span>
        </div>
      );
    }
    return null;
  };

  // Fallback input for no API key or errors
  const FallbackInput = ({ message }: { message?: string }) => (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
        <input
          ref={fallbackInputRef}
          type="text"
          value={inputValue}
          onChange={handleFallbackInputChange}
          onBlur={handleFallbackBlur}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full pl-10 pr-10 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-soft-cyan disabled:bg-bg-secondary disabled:text-text-muted"
        />
        {inputValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="flex items-center justify-between">
        {message && (
          <p className="text-xs text-amber-600">{message}</p>
        )}
        {renderSaveState()}
      </div>
    </div>
  );

  // No API key configured
  if (!apiKey) {
    return <FallbackInput message="Address autocomplete unavailable. Enter address manually." />;
  }

  // Loading error
  if (loadError) {
    return <FallbackInput message="Failed to load address autocomplete. Using manual entry." />;
  }

  // Still loading
  if (!isLoaded) {
    return (
      <div className="w-full px-3 py-2 border border-border-default rounded-lg bg-bg-secondary flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-text-muted" />
        <span className="text-text-muted text-sm">Loading address autocomplete...</span>
      </div>
    );
  }

  // Disabled state - show current value
  if (disabled) {
    return (
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
        <input
          type="text"
          value={inputValue}
          disabled
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-2 border border-border-default rounded-lg bg-bg-secondary text-text-muted"
        />
      </div>
    );
  }

  // Determine if we should show the search input
  const showSearch = singleAddressMode ? isEditing : true;
  const showSavedAddress = singleAddressMode && inputValue && !isEditing;

  return (
    <div className="space-y-2">
      {/* Show saved address with edit button (singleAddressMode only) */}
      {showSavedAddress && (
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-600" />
          <div className="w-full pl-10 pr-20 py-2 border border-green-300 bg-green-50 rounded-lg text-sm text-text-primary truncate">
            {inputValue}
          </div>
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {renderSaveState()}
            <button
              type="button"
              onClick={handleEdit}
              className="p-1.5 text-text-muted hover:text-text-primary hover:bg-green-100 rounded transition-colors"
              title="Change address"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="p-1.5 text-text-muted hover:text-red-500 hover:bg-red-50 rounded transition-colors"
              title="Remove address"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Show current address when NOT in singleAddressMode and has value */}
      {!singleAddressMode && inputValue && !isEditing && (
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-600" />
          <div className="w-full pl-10 pr-10 py-2 border border-green-300 bg-green-50 rounded-lg text-sm text-text-primary truncate">
            {inputValue}
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Search input - PlaceAutocompleteElement (Places API New) */}
      {showSearch && (
        <>
          {/* Cancel button when editing in singleAddressMode */}
          {singleAddressMode && isEditing && value && (
            <button
              type="button"
              onClick={handleCancelEdit}
              className="text-sm text-text-muted hover:text-text-primary"
            >
              Cancel
            </button>
          )}

          {/* Container for PlaceAutocompleteElement web component */}
          {/* Note: gmp-place-autocomplete has its own built-in search icon */}
          <div
            ref={setContainerRef}
            className="w-full min-h-[42px]"
            data-placeholder={placeholder}
          />

          <div className="flex items-center justify-between">
            <p className="text-xs text-text-muted">
              {singleAddressMode ? 'Search for your home address' : 'Start typing to search for an address'}
            </p>
            {renderSaveState()}
          </div>
        </>
      )}
    </div>
  );
}
